import type { ReactNode, Reducer } from 'react'
import React, { createContext, useContext, useReducer } from 'react'

import { isChecked } from '../utils/isChecked'

export type CheckedAssemblyItem = {
  id: string
  name: string
  imageUrl: string
  assemblyId: string
  seller: string
}

interface AssemblyItemState {
  values: CheckedAssemblyItem[]
  minQuantity: number
  maxQuantity: number
}

interface UpdateAssemblyItemStateAction {
  type: 'UPDATE_ASSEMBLY_ITEMS_STATE'
  payload: {
    value: CheckedAssemblyItem
    minQuantity: number
    maxQuantity: number
  }
}

type Action = UpdateAssemblyItemStateAction

const initialAssemblyItemState: AssemblyItemState = {
  values: [],
  minQuantity: 0,
  maxQuantity: 0,
}

const reducer: Reducer<AssemblyItemState, Action> = (state, action) => {
  switch (action.type) {
    case 'UPDATE_ASSEMBLY_ITEMS_STATE': {
      const { value, minQuantity, maxQuantity } = action.payload

      if (isChecked(state.values, value) && maxQuantity !== 1) {
        return {
          ...state,
          values: state.values.filter((v) => {
            return !(v.id === value.id && v.assemblyId === value.assemblyId)
          }),
          minQuantity,
          maxQuantity,
        }
      }

      if (maxQuantity === 1) {
        return {
          ...state,
          values: [
            ...state.values.filter((v) => v.assemblyId !== value.assemblyId),
            value,
          ],
          minQuantity,
          maxQuantity,
        }
      }

      return {
        ...state,
        values: [...state.values, value],
        minQuantity,
        maxQuantity,
      }
    }

    default:
      return state
  }
}

interface AssemblyItemContextProps {
  assemblyItemState: AssemblyItemState
  setAssemblyItemState: (
    value: CheckedAssemblyItem,
    minQuantity: number,
    maxQuantity: number
  ) => void
}

interface AssemblyItemsProviderProps {
  children: ReactNode
}

const AssemblyItemContext = createContext<AssemblyItemContextProps | undefined>(
  undefined
)

export const AssemblyItemsProvider: React.FC<AssemblyItemsProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, initialAssemblyItemState)

  const setAssemblyItemState = (
    value: CheckedAssemblyItem,
    minQuantity: number,
    maxQuantity: number
  ) => {
    dispatch({
      type: 'UPDATE_ASSEMBLY_ITEMS_STATE',
      payload: {
        value,
        minQuantity,
        maxQuantity,
      },
    })
  }

  const contextValue: AssemblyItemContextProps = {
    assemblyItemState: state,
    setAssemblyItemState,
  }

  return (
    <AssemblyItemContext.Provider value={contextValue}>
      {children}
    </AssemblyItemContext.Provider>
  )
}

export const useAssemblyItemsContext = (): AssemblyItemContextProps => {
  const context = useContext(AssemblyItemContext)

  if (!context) {
    throw new Error(
      'useAssemblyItemsContext must be used within an AssemblyItemsProvider'
    )
  }

  return context
}
